import {
	AlertTriangle,
	Building2,
	Check,
	ChevronDown,
	ChevronRight,
	MoveRight,
	Pencil,
	RotateCcw,
	Trash2,
	X,
	Zap,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { cn } from '@/lib/utils'
import type { OrgBranch, OrgCR } from '@/types/organization'

interface OrgTreeProps {
	branches: OrgBranch[]
	allBranches: OrgBranch[]
	viewFilter?: 'all' | 'erp' | 'virtual'
	onRenameBranch: (branchId: string, newName: string | null) => Promise<void>
	onDeleteBranch: (branchId: string) => Promise<void>
	onToggleVisibility: (branchId: string, isHidden: boolean) => Promise<void>
	onRenameCR: (crId: string, newName: string | null) => Promise<void>
	onMoveCR: (crId: string, targetBranchId: string | null) => Promise<void>
}

// Virtual branches are identified by the "SYS-" code prefix
function isVirtualBranch(code: string | null | undefined): boolean {
	return (code ?? '').startsWith('SYS-')
}

// ── Inline name editor ──────────────────────────────────────────────────────

interface InlineRenameProps {
	currentName: string
	onSave: (newName: string | null) => Promise<void>
	resetLabel: string
}

function InlineRename({ currentName, onSave, resetLabel }: InlineRenameProps) {
	const [editing, setEditing] = useState(false)
	const [value, setValue] = useState(currentName)
	const [loading, setLoading] = useState(false)

	const handleSave = async () => {
		setLoading(true)
		await onSave(value.trim() || null)
		setLoading(false)
		setEditing(false)
	}

	const handleCancel = () => {
		setValue(currentName)
		setEditing(false)
	}

	const handleReset = async () => {
		setLoading(true)
		await onSave(null)
		setLoading(false)
		setEditing(false)
	}

	if (!editing) {
		return (
			<Button
				type="button"
				variant="ghost"
				size="icon-circle"
				onClick={() => {
					setValue(currentName)
					setEditing(true)
				}}
				className="shrink-0 text-kaefer-dark-grey hover:bg-transparent hover:text-kaefer-dark-grey/70"
				title={resetLabel}
			>
				<Pencil className="size-3.5" />
			</Button>
		)
	}

	return (
		<div className="flex items-center gap-1.5 flex-1">
			<Input
				autoFocus
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="h-7 text-sm py-0 flex-1"
				onKeyDown={(e) => {
					if (e.key === 'Enter') handleSave()
					if (e.key === 'Escape') handleCancel()
				}}
				maxLength={100}
			/>
			<Button
				type="button"
				variant="ghost"
				size="icon-circle"
				onClick={handleSave}
				disabled={loading}
				className="text-grass-green hover:bg-transparent hover:text-grass-green/80 shrink-0"
			>
				<Check className="size-3.5" />
			</Button>
			<Button
				type="button"
				variant="ghost"
				size="icon-circle"
				onClick={handleReset}
				disabled={loading}
				title={resetLabel}
				className="text-amber-500 hover:bg-transparent hover:text-amber-500/80 shrink-0"
			>
				<RotateCcw className="size-3.5" />
			</Button>
			<Button
				type="button"
				variant="ghost"
				size="icon-circle"
				onClick={handleCancel}
				disabled={loading}
				className="text-muted-foreground hover:bg-transparent hover:text-muted-foreground/80 shrink-0"
			>
				<X className="size-3.5" />
			</Button>
		</div>
	)
}

// ── CR row with inline rename + branch move select ──────────────────────────

interface CRRowProps {
	cr: OrgCR
	allBranches: OrgBranch[]
	currentBranchId: string
	onRenameCR: (crId: string, newName: string | null) => Promise<void>
	onMoveCR: (crId: string, targetBranchId: string | null) => Promise<void>
}

function CRRow({
	cr,
	allBranches,
	currentBranchId,
	onRenameCR,
	onMoveCR,
}: CRRowProps) {
	const { t } = useTranslation()
	const [movingTo, setMovingTo] = useState('')
	const [showMoveSelect, setShowMoveSelect] = useState(false)
	const [moveLoading, setMoveLoading] = useState(false)

	const handleMoveConfirm = async () => {
		setMoveLoading(true)
		await onMoveCR(cr.id, movingTo || null)
		setMoveLoading(false)
		setShowMoveSelect(false)
		setMovingTo('')
	}

	return (
		<div className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted/20 group min-h-10">
			{/* CR name */}
			<div className="flex items-center gap-2 flex-1 min-w-0">
				<span className="text-sm text-kaefer-dark-grey truncate">
					{cr.name}
				</span>
			</div>

			{/* Actions */}
			<div className="flex items-center gap-1 shrink-0">
				{/* Inline rename */}
				<InlineRename
					currentName={cr.name}
					onSave={(newName) => onRenameCR(cr.id, newName)}
					resetLabel={t('administration.orgOverrides.tree.resetName')}
				/>

				{/* Move to branch */}
				{!showMoveSelect ? (
					<Button
						type="button"
						variant="ghost"
						size="icon-circle"
						onClick={() => setShowMoveSelect(true)}
						className="shrink-0 text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-medium-grey/70"
						title={t('administration.orgOverrides.tree.moveCR')}
					>
						<MoveRight className="size-3.5" />
					</Button>
				) : (
					<div className="flex items-center gap-1.5">
						<NativeSelect
							value={movingTo}
							onChange={(e) => setMovingTo(e.target.value)}
							className="h-7 text-xs py-0 w-36"
						>
							<NativeSelectOption value="">
								{t('administration.orgOverrides.tree.originalBranch')}
							</NativeSelectOption>
							{allBranches
								.filter((b) => b.id !== currentBranchId)
								.map((b) => (
									<NativeSelectOption key={b.id} value={b.id}>
										{b.name}
									</NativeSelectOption>
								))}
						</NativeSelect>
						<Button
							type="button"
							variant="ghost"
							size="icon-circle"
							onClick={handleMoveConfirm}
							disabled={moveLoading}
							className="text-grass-green hover:bg-transparent hover:text-grass-green/80 shrink-0"
						>
							<Check className="size-3.5" />
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="icon-circle"
							onClick={() => {
								setShowMoveSelect(false)
								setMovingTo('')
							}}
							disabled={moveLoading}
							className="text-muted-foreground hover:bg-transparent shrink-0"
						>
							<X className="size-3.5" />
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}

// ── Branch row (collapsible) ─────────────────────────────────────────────────

interface BranchRowProps {
	branch: OrgBranch
	allBranches: OrgBranch[]
	onRenameBranch: (branchId: string, newName: string | null) => Promise<void>
	onDeleteBranch: (branchId: string) => Promise<void>
	onToggleVisibility: (branchId: string, isHidden: boolean) => Promise<void>
	onRenameCR: (crId: string, newName: string | null) => Promise<void>
	onMoveCR: (crId: string, targetBranchId: string | null) => Promise<void>
}

function BranchRow({
	branch,
	allBranches,
	onRenameBranch,
	onDeleteBranch,
	onToggleVisibility,
	onRenameCR,
	onMoveCR,
}: BranchRowProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [visibilityLoading, setVisibilityLoading] = useState(false)
	const [deleteLoading, setDeleteLoading] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const virtual = isVirtualBranch(branch.code)

	const handleToggleVisibility = async (e: React.MouseEvent) => {
		e.stopPropagation()
		setVisibilityLoading(true)
		await onToggleVisibility(branch.id, !branch.isHidden)
		setVisibilityLoading(false)
	}

	const handleDeleteBranchClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		setConfirmingDelete(true)
	}

	const handleConfirmDeleteBranch = async () => {
		setDeleteLoading(true)
		await onDeleteBranch(branch.id)
		setDeleteLoading(false)
		setConfirmingDelete(false)
	}

	return (
		<div className={cn('rounded-lg border overflow-hidden')}>
			{/* Branch header */}
			<div
				className={cn(
					'flex items-center gap-2 px-4 py-3 cursor-pointer select-none',
					'bg-background border-b border-kaefer-light-silver/40',
				)}
				onClick={() => setOpen((o) => !o)}
			>
				{open ? (
					<ChevronDown className="size-4 text-kaefer-medium-grey shrink-0" />
				) : (
					<ChevronRight className="size-4 text-kaefer-medium-grey shrink-0" />
				)}

				{virtual && (
					<Zap
						className="size-3.5 text-astral-blue shrink-0"
						aria-label={t('administration.orgOverrides.tree.virtualBranch')}
					/>
				)}

				<div className="flex items-center gap-2 flex-1 min-w-0">
					<span
						className={cn(
							'text-sm  text-kaefer-dark-grey truncate',
							branch.isHidden ? 'line-through text-kaefer-medium-grey/60' : '',
						)}
					>
						{branch.name}
					</span>
					<span
						className={cn(
							'text-xs text-muted-foreground shrink-0',
							branch.isHidden ? 'line-through text-kaefer-medium-grey/60' : '',
						)}
					>
						({branch.crs.length} CRs)
					</span>
					{branch.isHidden && branch.crs.length > 0 && (
						<span
							className="flex items-center gap-1 shrink-0 rounded-full bg-kaefer-honey-yellow/15 px-2 py-0.5 text-[11px] font-medium text-kaefer-pumpkin-orange"
							title={t('administration.orgOverrides.tree.hiddenWithCRsWarning')}
						>
							<AlertTriangle className="size-3" />
							{t('administration.orgOverrides.tree.hiddenWithCRsWarning')}
						</span>
					)}
				</div>

				{/* Actions — stop propagation to not toggle accordion */}
				<div
					onClick={(e) => e.stopPropagation()}
					className="flex items-center gap-1 shrink-0"
				>
					<button
						type="button"
						onClick={handleToggleVisibility}
						disabled={visibilityLoading}
						aria-label={t(
							branch.isHidden
								? 'administration.orgOverrides.tree.showBranch'
								: 'administration.orgOverrides.tree.hideBranch',
						)}
						title={t(
							branch.isHidden
								? 'administration.orgOverrides.tree.showBranch'
								: 'administration.orgOverrides.tree.hideBranch',
						)}
						className={cn(
							'inline-flex items-center gap-pxborder rounded-lg p-0.5 transition-colors  disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ',
							branch.isHidden
								? 'border-kaefer-red border bg-kaefer-red/10'
								: 'border-kaefer-grass-green border bg-kaefer-grass-green/10',
						)}
					>
						<span
							className={cn(
								'px-2.5 py-1 rounded-md text-[11px] font-medium leading-none transition-all',
								!branch.isHidden
									? 'bg-kaefer-grass-green text-white shadow-sm'
									: 'text-kaefer-dark-grey',
							)}
						>
							{t('administration.orgOverrides.tree.visibilityVisible')}
						</span>
						<span
							className={cn(
								'px-2.5 py-1 rounded-md text-[11px] font-medium leading-none transition-all',
								branch.isHidden
									? 'bg-kaefer-red text-white shadow-sm'
									: 'text-kaefer-dark-grey',
							)}
						>
							{t('administration.orgOverrides.tree.visibilityHidden')}
						</span>
					</button>
					<InlineRename
						currentName={branch.name}
						onSave={(newName) => onRenameBranch(branch.id, newName)}
						resetLabel={t('administration.orgOverrides.tree.resetName')}
					/>
					{virtual && (
						<Button
							type="button"
							variant="ghost"
							size="icon-circle"
							onClick={handleDeleteBranchClick}
							disabled={deleteLoading}
							className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/80 shrink-0"
							title={t('administration.orgOverrides.tree.deleteBranch')}
						>
							<Trash2 className="size-3.5" />
						</Button>
					)}
				</div>
			</div>

			{/* CRs list */}
			{open && (
				<div className="flex flex-col divide-y divide-kaefer-light-silver/30">
					{branch.crs.length === 0 ? (
						<p className="px-4 py-3 text-sm text-muted-foreground text-center">
							{t('administration.orgOverrides.tree.noCRs')}
						</p>
					) : (
						branch.crs.map((cr) => (
							<CRRow
								key={cr.id}
								cr={cr}
								allBranches={allBranches}
								currentBranchId={branch.id}
								onRenameCR={onRenameCR}
								onMoveCR={onMoveCR}
							/>
						))
					)}
				</div>
			)}

			<ConfirmDialog
				open={confirmingDelete}
				onOpenChange={setConfirmingDelete}
				title={t('administration.orgOverrides.tree.deleteBranch')}
				onConfirm={handleConfirmDeleteBranch}
				isPending={deleteLoading}
				confirmLabel={t('common.delete')}
				cancelLabel={t('common.cancel')}
				destructive
			>
				{t('administration.orgOverrides.tree.deleteBranchConfirm', {
					name: branch.name,
				})}
			</ConfirmDialog>
		</div>
	)
}

// ── Section divider ──────────────────────────────────────────────────────────

function SectionDivider({
	icon,
	label,
	count,
}: {
	icon: ReactNode
	label: string
	count: number
}) {
	return (
		<div className="flex items-center gap-2 py-1">
			<div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide shrink-0">
				{icon}
				{label}
				<span className="font-normal normal-case tracking-normal bg-muted px-1.5 py-0.5 rounded">
					{count}
				</span>
			</div>
			<div className="flex-1 h-px bg-kaefer-light-silver/50" />
		</div>
	)
}

// ── Main tree component ──────────────────────────────────────────────────────

export function OrgTree({
	branches,
	allBranches,
	viewFilter = 'all',
	onRenameBranch,
	onDeleteBranch,
	onToggleVisibility,
	onRenameCR,
	onMoveCR,
}: OrgTreeProps) {
	const { t } = useTranslation()

	if (branches.length === 0) {
		return (
			<p className="text-center text-sm text-muted-foreground py-10">
				{t('administration.orgOverrides.tree.noData')}
			</p>
		)
	}

	const renderBranch = (branch: OrgBranch) => (
		<BranchRow
			key={branch.id}
			branch={branch}
			allBranches={allBranches}
			onRenameBranch={onRenameBranch}
			onDeleteBranch={onDeleteBranch}
			onToggleVisibility={onToggleVisibility}
			onRenameCR={onRenameCR}
			onMoveCR={onMoveCR}
		/>
	)

	if (viewFilter === 'all') {
		const erpBranches = branches.filter((b) => !isVirtualBranch(b.code))
		const virtualBranches = branches.filter((b) => isVirtualBranch(b.code))

		if (erpBranches.length > 0 && virtualBranches.length > 0) {
			return (
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-2">
						<SectionDivider
							icon={<Building2 className="size-3.5" />}
							label={t('administration.orgOverrides.sectionErp')}
							count={erpBranches.length}
						/>
						{erpBranches.map(renderBranch)}
					</div>
					<div className="flex flex-col gap-2 mt-3">
						<SectionDivider
							icon={<Zap className="size-3.5" />}
							label={t('administration.orgOverrides.sectionVirtual')}
							count={virtualBranches.length}
						/>
						{virtualBranches.map(renderBranch)}
					</div>
				</div>
			)
		}
	}

	return <div className="flex flex-col gap-2">{branches.map(renderBranch)}</div>
}
